import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import { trackClick } from "../lib/analytics";
import { Heart, MessageSquare, ArrowRight, ExternalLink, ChevronLeft, Gauge, Activity, Wrench, Clock, ShieldCheck, Sparkles } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso);
  if (isNaN(t.getTime())) return null;
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function useLikedFlag(slug) {
  const key = `stt.liked.${slug}`;
  const [liked, setLiked] = useState(() => !!localStorage.getItem(key));
  const mark = () => { localStorage.setItem(key, "1"); setLiked(true); };
  return [liked, mark];
}

const TakeBox = ({ take }) => (
  <section
    className="mt-6 rounded-xl p-5 sm:p-6 border-l-2"
    style={{ background: "rgba(61,107,255,0.05)", borderColor: "var(--cobalt-bright)" }}
    data-testid="stt-take"
  >
    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-3">
      <Sparkles className="w-3.5 h-3.5" /> SpeedTestTools' Take
    </div>
    {take ? (
      <div className="space-y-3 text-sm sm:text-base leading-relaxed text-[var(--text)]" data-testid="stt-take-body">
        {take.split(/\n\s*\n/).map((p, i) => <p key={i}>{p.trim()}</p>)}
      </div>
    ) : (
      <p className="text-sm text-[var(--text-muted)] animate-pulse" data-testid="stt-take-pending">Our editors' take on this story is being written — check back in a minute.</p>
    )}
  </section>
);

export default function NewsArticle() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comments, setComments] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("stt.commentName") || "");
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [postErr, setPostErr] = useState(null);
  const [liked, markLiked] = useLikedFlag(slug);
  const [likeCount, setLikeCount] = useState(0);

  const load = useCallback(async () => {
    setNotFound(false);
    try {
      const r = await api.get(`/news/article/${slug}`);
      setData(r.data);
      setLikeCount(r.data.article.likes || 0);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
  }, [slug]);

  const loadComments = useCallback(async () => {
    try {
      const r = await api.get(`/news/article/${slug}/comments`);
      setComments(r.data.comments || []);
    } catch {}
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    load();
    loadComments();
  }, [load, loadComments]);

  const like = async () => {
    if (liked) return;
    trackClick("news-like", { slug });
    try {
      const r = await api.post(`/news/article/${slug}/like`);
      setLikeCount(r.data.likes);
      markLiked();
    } catch {}
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (posting) return;
    setPostErr(null);
    setPosting(true);
    try {
      const r = await api.post(`/news/article/${slug}/comment`, { name: name.trim(), body: body.trim() });
      localStorage.setItem("stt.commentName", name.trim());
      setBody("");
      setComments((prev) => [r.data.comment, ...prev]);
      trackClick("news-comment", { slug });
    } catch (e2) {
      setPostErr(e2?.response?.data?.detail || "Couldn't post that. Try again in a minute.");
    } finally {
      setPosting(false);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-16 text-center">
        <Helmet><title>Story not found · SpeedTestTools</title></Helmet>
        <h1 className="font-display text-2xl font-semibold mb-3">We couldn't find that story.</h1>
        <p className="text-[var(--text-dim)] mb-6">It may have been removed or the link is broken.</p>
        <Link to="/news" className="btn-cobalt inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to News
        </Link>
      </div>
    );
  }

  if (!data) {
    return <div className="max-w-3xl mx-auto px-5 py-16 text-[var(--text-muted)] text-sm">Loading…</div>;
  }

  const a = data.article;
  const canonical = `https://speedtesttools.com/news/${a.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: a.title,
    datePublished: a.published || a.first_seen,
    dateModified: a.first_seen,
    author: [{ "@type": "Organization", name: "SpeedTestTools Editorial" }],
    publisher: { "@type": "Organization", name: "SpeedTestTools", url: "https://speedtesttools.com" },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    isBasedOn: a.source_link,
    ...(a.stt_take ? { articleBody: a.stt_take } : {}),
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-12">
      <Helmet>
        <title>{`${a.title} · SpeedTestTools`}</title>
        <meta name="description" content={a.stt_take ? a.stt_take.replace(/\s+/g, " ").slice(0, 155) : `Latest report from ${a.source || "the web"} on ${a.title}. Read the full story and see what other readers are saying.`} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={a.title} />
        <meta property="og:description" content={`Reported by ${a.source || "the web"}. Discuss it on SpeedTestTools.`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Link to="/news" onClick={() => trackClick("news-back")} className="inline-flex items-center gap-1 text-xs font-mono text-[var(--text-muted)] hover:text-white transition mb-4">
        <ChevronLeft className="w-3.5 h-3.5" /> All news
      </Link>

      <article className="glass-strong rounded-2xl p-5 sm:p-7 mb-6">
        <div className="flex items-center flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider text-[var(--cobalt-bright)] mb-4">
          <span>{a.source || "News"}</span>
          {a.first_seen && <><span className="text-[var(--text-muted)]">·</span><Clock className="w-3 h-3 text-[var(--text-muted)]" /><span className="text-[var(--text-muted)]">{timeAgo(a.first_seen)}</span></>}
          <span className="text-[var(--text-muted)]">·</span>
          <span className="text-[var(--text-muted)]">SpeedTestTools</span>
        </div>

        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-[1.15]">
          {a.title}
        </h1>

        <p className="mt-5 text-sm sm:text-base text-[var(--text-dim)] leading-relaxed">
          {a.source || "A publisher"} is reporting on the story above. It caught our attention because it may affect readers checking their internet or a specific service on SpeedTestTools right now.
        </p>

        <TakeBox take={a.stt_take} />

        <a
          href={a.source_link}
          target="_blank"
          rel="noreferrer nofollow"
          onClick={() => trackClick("news-source-open", { slug: a.slug })}
          className="btn-cobalt mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium"
          data-testid="news-source-link"
        >
          <ExternalLink className="w-4 h-4" /> Read the full story at {a.source || "the source"}
        </a>

        {/* What you can do */}
        <div className="mt-8 rounded-xl p-5 border" style={{ background: "rgba(61,107,255,0.06)", borderColor: "rgba(61,107,255,0.25)" }}>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-3">If this affects you, right now</div>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3"><Gauge className="w-4 h-4 text-[var(--cobalt-bright)] mt-0.5 shrink-0" /><span>Run a <Link to="/speed-test" className="underline hover:text-[var(--cobalt-bright)]">quick speed test</Link> — get concrete numbers before assuming it's your ISP.</span></li>
            <li className="flex gap-3"><Activity className="w-4 h-4 text-[var(--cobalt-bright)] mt-0.5 shrink-0" /><span>Open the <Link to="/down-detector" className="underline hover:text-[var(--cobalt-bright)]">Down Detector board</Link> to see which major services are actually having issues right now.</span></li>
            <li className="flex gap-3"><Wrench className="w-4 h-4 text-[var(--cobalt-bright)] mt-0.5 shrink-0" /><span>If numbers look off, walk through the <Link to="/fix-my-internet" className="underline hover:text-[var(--cobalt-bright)]">Fix-My-Internet checklist</Link> — most issues clear in the first two steps.</span></li>
          </ul>
        </div>

        {/* Actions */}
        <div className="mt-8 flex items-center gap-3">
          <button
            onClick={like}
            disabled={liked}
            data-testid="news-like-btn"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition ${
              liked
                ? "bg-rose-500/15 border-rose-500/40 text-rose-300 cursor-default"
                : "bg-white/5 border-white/10 hover:bg-rose-500/10 hover:border-rose-500/30 hover:text-rose-300"
            }`}
          >
            <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} />
            {likeCount} {liked ? "liked" : "like"}
          </button>
          <div className="text-xs text-[var(--text-muted)] font-mono flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Comments below — no login needed
          </div>
        </div>
      </article>

      {/* Comments */}
      <section className="glass rounded-2xl p-5 sm:p-6 mb-6" data-testid="news-comments">
        <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[var(--cobalt-bright)]" /> Discussion
          <span className="text-xs font-mono text-[var(--text-muted)]">({comments.length})</span>
        </h2>

        <form onSubmit={submitComment} className="space-y-3 mb-6" data-testid="news-comment-form">
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            maxLength={40}
            className="w-full bg-[rgba(15,17,32,0.7)] border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[var(--cobalt)] transition"
            data-testid="comment-name-input"
          />
          <textarea
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Share your take, ask a question, or report what you're seeing…"
            maxLength={1000}
            rows={3}
            className="w-full bg-[rgba(15,17,32,0.7)] border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[var(--cobalt)] transition resize-none"
            data-testid="comment-body-input"
          />
          {postErr && <div className="text-sm text-rose-300" data-testid="comment-error">{postErr}</div>}
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] font-mono text-[var(--text-muted)]">Be nice · no spam · we auto-block profanity</span>
            <button type="submit" disabled={posting || !name.trim() || body.trim().length < 5} className="btn-cobalt inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed" data-testid="comment-submit-btn">
              {posting ? "Posting…" : "Post comment"}
            </button>
          </div>
        </form>

        {comments.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] py-4">Be the first to comment.</div>
        ) : (
          <ul className="space-y-4" data-testid="comment-list">
            {comments.map((c) => (
              <li key={c.id} className="p-4 rounded-lg border border-white/8 bg-[rgba(15,17,32,0.5)]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold">{c.name}</span>
                  <span className="text-[11px] font-mono text-[var(--text-muted)]">· {timeAgo(c.ts)}</span>
                </div>
                <p className="text-sm text-[var(--text-dim)] leading-relaxed whitespace-pre-wrap break-words">{c.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Related */}
      {data.related && data.related.length > 0 && (
        <section className="glass rounded-2xl p-5 sm:p-6" data-testid="news-related">
          <h2 className="font-display text-lg font-semibold mb-4">Related stories</h2>
          <ul className="space-y-3">
            {data.related.map((r) => (
              <li key={r.slug}>
                <Link to={`/news/${r.slug}`} onClick={() => trackClick("news-related-open", { slug: r.slug })} className="block p-3 rounded-lg hover:bg-[rgba(61,107,255,0.08)] transition">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--cobalt-bright)] mb-1">{r.source || "News"}</div>
                  <div className="text-sm font-medium">{r.title}</div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
